import * as pdfjsLib from 'pdfjs-dist';

/**
 * Read a source PDF's own document-tier metadata — Title and Language — IN THE
 * BROWSER, so the editor can seed those two fields the instant a file is selected,
 * before the upload round-trip. The backend still re-reads and confirms the same
 * values on upload (PDFUploadController.extractSourceDocTier); this is the no-flicker
 * head start, not a second source of truth.
 *
 * Precedence mirrors the backend exactly:
 *   - Title:    XMP dc:title wins over the Info dictionary /Title.
 *   - Language: the catalog /Lang (pdf.js surfaces it as info.Language).
 *
 * Best-effort by design: any failure (unreadable file, encrypted PDF, missing
 * metadata, a test stub without getMetadata) yields {} and the caller's
 * filename-derived guess stands. It never throws.
 */
export interface SourceDocTier {
  /** The document's own title, trimmed, if it declared one. */
  title?: string;
  /** The document's declared language tag (e.g. "en-US", "es-ES"), trimmed, if present. */
  language?: string;
}

// Configure the PDF.js worker even if PDFViewer has not mounted yet — the same
// local worker PDFViewer uses (copied into public/ by scripts/copy-pdf-worker.js
// on prestart/prebuild, so it works offline / air-gapped). Guarded so we never
// clobber a worker source PDFViewer already set.
if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;
}

export async function readSourcePdfDocTier(file: File): Promise<SourceDocTier> {
  try {
    const data = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data }).promise;
    try {
      // The test stub (src/__mocks__/pdfjsStub.ts) may not implement getMetadata.
      const getMetadata = (pdf as any).getMetadata;
      if (typeof getMetadata !== 'function') return {};
      const meta = await getMetadata.call(pdf);
      const result: SourceDocTier = {};

      // Title — dc:title (XMP) beats the Info dictionary /Title, same as the backend.
      let title: string | undefined;
      const xmp = meta?.metadata;
      if (xmp && typeof xmp.get === 'function') {
        const dcTitle = xmp.get('dc:title');
        if (typeof dcTitle === 'string' && dcTitle.trim()) title = dcTitle.trim();
      }
      if (!title) {
        const infoTitle = meta?.info?.Title;
        if (typeof infoTitle === 'string' && infoTitle.trim()) title = infoTitle.trim();
      }
      if (title) result.title = title;

      // Language — the catalog /Lang, surfaced by pdf.js as info.Language. We pass it
      // through verbatim; the caller matches the editor's dropdown case-insensitively
      // (so "EN-US" still lands on the en-US entry), and the backend canonicalizes on
      // upload. No BCP-47 normalization is duplicated here.
      const lang = meta?.info?.Language;
      if (typeof lang === 'string' && lang.trim()) result.language = lang.trim();

      return result;
    } finally {
      try {
        await (pdf as any).destroy?.();
      } catch {
        /* a failed cleanup must not mask a good read */
      }
    }
  } catch {
    return {};
  }
}
